import React, {useState} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import Icon from '@/shared/components/Icon';
import Button from '@/shared/components/Button';
import membershipService from '../services/membershipService';
import {useMembershipPlans} from '../hooks/useMembershipPlans';
import {USER_DATA_KEY} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {logError} from '@/shared/utils/logger';

interface Props {
    isSubscribed: boolean;
    onClose: () => void;
}

const MEMBERSHIP_QUERY_KEY = ['membership', 'me'];

function formatDate(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? iso
        : d.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'});
}

/**
 * Modal opened by the "Manage Membership" link in HealthProfileCard.
 * Members see their plan + renewal/cancellation date and a self-service
 * cancel CTA (Task 10); the sign-up path is `MembershipBenefitsView` /
 * `MembershipCheckoutModal`, not this modal. Visual contract follows
 * STYLE_GUIDE §8.
 */
const ManageMembershipModal: React.FC<Props> = ({isSubscribed, onClose}) => {
    const queryClient = useQueryClient();
    const [confirming, setConfirming] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const {data: membership, isLoading} = useQuery({
        queryKey: MEMBERSHIP_QUERY_KEY,
        queryFn: () => membershipService.getMine(),
        enabled: isSubscribed,
    });

    const membershipPlans = useMembershipPlans();

    const planLabel = membership
        ? (membershipPlans.find((p) => p.id === membership.plan)?.label ?? membership.plan)
        : null;

    const handleCancel = async () => {
        setCancelling(true);
        setError(null);
        try {
            const updated = await membershipService.cancel();
            queryClient.setQueryData(MEMBERSHIP_QUERY_KEY, updated);
            queryClient.invalidateQueries({queryKey: [USER_DATA_KEY]});
            toast.success(`Your membership will end on ${formatDate(updated.currentPeriodEnd)}.`);
            setConfirming(false);
        } catch (err) {
            logError('Membership cancel failed', err);
            setError(
                'Something went wrong cancelling your membership. Please try again or contact support@mylabsdirect.com.'
            );
        } finally {
            setCancelling(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/30" onClick={onClose} />
            <div className="relative w-full max-w-md rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <h2 className="text-lg font-semibold text-mld-text-primary">Manage Membership</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors">
                        <Icon name="xmark" className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-mld-primary/10 text-mld-primary">
                            <Icon name="sparkles" className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-mld-text-primary">
                                {isSubscribed ? 'My Labs+ Member' : 'Join My Labs+'}
                            </p>
                            <p className="text-xs text-gray-500">
                                {isSubscribed
                                    ? 'You have access to advanced lab insights and member pricing.'
                                    : 'Become a member to unlock advanced lab insights and member pricing.'}
                            </p>
                        </div>
                    </div>

                    {!isSubscribed && (
                        <div className="rounded-xl border border-mld-panel-border bg-mld-primary/5 p-4 text-sm text-mld-text-primary">
                            Membership sign-up is available from the My Labs+ page. To learn more about member benefits,
                            contact support@mylabsdirect.com.
                        </div>
                    )}

                    {isSubscribed && isLoading && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Icon name="spinner" spin className="h-4 w-4" />
                            Loading your membership…
                        </div>
                    )}

                    {isSubscribed && !isLoading && membership && (
                        <div className="rounded-xl border border-mld-panel-border bg-mld-primary/5 p-4 text-sm text-mld-text-primary">
                            <dl className="space-y-1">
                                <div className="flex items-center justify-between">
                                    <dt className="text-gray-500">Plan</dt>
                                    <dd className="font-medium">{planLabel}</dd>
                                </div>
                                <div className="flex items-center justify-between">
                                    <dt className="text-gray-500">
                                        {membership.cancelAtPeriodEnd ? 'Cancels on' : 'Renews on'}
                                    </dt>
                                    <dd className="font-medium">{formatDate(membership.currentPeriodEnd)}</dd>
                                </div>
                            </dl>
                            {membership.cancelAtPeriodEnd && (
                                <p className="mt-3 text-xs text-gray-500">
                                    Your membership is set to end on {formatDate(membership.currentPeriodEnd)}. You'll
                                    keep member access until then.
                                </p>
                            )}
                        </div>
                    )}

                    {isSubscribed && !isLoading && !membership && (
                        <div className="rounded-xl border border-mld-panel-border bg-mld-primary/5 p-4 text-sm text-mld-text-primary">
                            No active membership was found. If you believe this is an error, contact
                            support@mylabsdirect.com.
                        </div>
                    )}

                    {confirming && (
                        <div className="rounded-xl border border-mld-danger/20 bg-mld-danger/5 p-4 text-sm text-mld-text-primary">
                            <p className="font-medium">Cancel your My Labs+ membership?</p>
                            <p className="mt-1 text-gray-500">
                                You'll keep member access until{' '}
                                {membership
                                    ? formatDate(membership.currentPeriodEnd)
                                    : 'the end of your billing period'}
                                , then your membership will end.
                            </p>
                        </div>
                    )}

                    {error && (
                        <div className="rounded-xl border border-mld-danger/20 bg-mld-danger/5 p-4 text-sm text-mld-danger">
                            {error}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                    {isSubscribed && membership && !membership.cancelAtPeriodEnd && confirming && (
                        <>
                            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={cancelling}>
                                Keep Membership
                            </Button>
                            <Button variant="destructive" onClick={handleCancel} loading={cancelling}>
                                Confirm Cancel
                            </Button>
                        </>
                    )}
                    {isSubscribed && membership && !membership.cancelAtPeriodEnd && !confirming && (
                        <>
                            <Button variant="secondary" onClick={onClose}>
                                Close
                            </Button>
                            <Button variant="destructive" onClick={() => setConfirming(true)}>
                                Cancel Membership
                            </Button>
                        </>
                    )}
                    {(!isSubscribed || !membership || membership.cancelAtPeriodEnd) && (
                        <Button variant="secondary" onClick={onClose}>
                            Close
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManageMembershipModal;
